import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { isAfter } from 'date-fns/isAfter'
import { isSameDay } from 'date-fns/isSameDay'
import { startOfDay } from 'date-fns/startOfDay'
import { isEqual } from 'date-fns/isEqual'
import { isBefore } from 'date-fns/isBefore'
import { Locale } from 'date-fns/locale/types'
import { useEffect, useState } from 'react'

export function todayPlusTwoDays(): Date {
  return addDays(today(), 2)
}

export function today(): Date {
  return startOfDay(new Date())
}

export function isTodayOrTomorrow(day: Date): boolean {
  return isSameDay(day, today()) || isSameDay(day, addDays(today(), 1))
}

export function inOneYear(): Date {
  return addMonths(today(), 12)
}

export function dayIsOutsideBookingWindow(): (day: Date) => boolean {
  return (day) =>
    isBefore(day, todayPlusTwoDays()) || isEqual(day, inOneYear()) || isAfter(day, inOneYear())
}

export function isSameOrAfterDay(dateLeft, dateRight) {
  return isSameDay(dateLeft, dateRight) || isAfter(dateLeft, dateRight)
}

export function isSameOrBeforeDay(dateLeft, dateRight) {
  return isSameDay(dateLeft, dateRight) || isBefore(dateLeft, dateRight)
}

export async function loadDateFnsLocale(locale: string): Promise<Locale> {
  switch (locale) {
    case 'de':
      return import('date-fns/locale/de').then((module) => module.default as unknown as Locale)
    case 'en':
      return import('date-fns/locale/en-GB').then((module) => module.default as unknown as Locale)
    case 'es':
      return import('date-fns/locale/es').then((module) => module.default as unknown as Locale)
    case 'fr':
      return import('date-fns/locale/fr').then((module) => module.default as unknown as Locale)
    case 'it':
      return import('date-fns/locale/it').then((module) => module.default as unknown as Locale)
    case 'nl':
      return import('date-fns/locale/nl').then((module) => module.default as unknown as Locale)
    case 'pl':
      return import('date-fns/locale/pl').then((module) => module.default as unknown as Locale)
    case 'sv':
      return import('date-fns/locale/sv').then((module) => module.default as unknown as Locale)
    default:
      return import('date-fns/locale/de').then((module) => module.default as unknown as Locale)
  }
}

declare global {
  interface Window {
    locale: Locale | undefined
  }
}

export function useDateFnsLocale(userLocale: string) {
  const [locale, _setLocale] = useState<Locale | undefined>(
    typeof window === 'undefined' ? undefined : window.locale,
  )

  function setLocale(locale: Locale) {
    window.locale = locale
    _setLocale(locale)
  }

  useEffect(() => {
    void loadDateFnsLocale(userLocale).then(setLocale)
  }, [])

  return locale
}
